import React, { useCallback, useEffect, useState, useRef } from "react";
import axiosInstance from "../../../../../services/AxiosInstance";
import { notifyError } from "jsx/components/CitoPlus/components/CitoPlusToastr";
import Tables from "jsx/components/CitoPlus/components/tables";
import Select from "react-select";
import ZonaSocialSelectReact from "jsx/components/CitoPlus/components/ZonaSocial/ZonaSocialSelectReact";
import ActivosSelectReact from "jsx/components/CitoPlus/components/Activos/ActivosSelectReact";
import moment from "moment";
import { Button } from "react-bootstrap";

const HISTORY_URL = "/api/v3/zonas-sociales/activos/historial/";
const CATEGORIAS_URL = "/api/v3/zonas-sociales/activos/categorias/";

const ACTION_LABELS = {
  create: { label: "Creación", className: "bg-success" },
  update: { label: "Actualización", className: "bg-info" },
  delete: { label: "Eliminación", className: "bg-danger" },
};

const ACTION_OPTIONS = [
  { value: "all", label: "Todas" },
  { value: "create", label: "Creación" },
  { value: "update", label: "Actualización" },
  { value: "delete", label: "Eliminación" },
];

const HistorialActivos = () => {
  const [historial, setHistorial] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Filtros
  const [zonaFilter, setZonaFilter] = useState("all");
  const [activoFilter, setActivoFilter] = useState("all");
  const [categoriaFilter, setCategoriaFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTermDebounced, setSearchTermDebounced] = useState("");
  const [actionFilter, setActionFilter] = useState("all");

  // Categorías
  const [categorias, setCategorias] = useState([]);

  // Paginación
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [previousUrl, setPreviousUrl] = useState(null);

  // Ref para mantener valores actuales sin causar re-renders
  const filtersRef = useRef({
    zonaFilter: "all",
    activoFilter: "all",
    categoriaFilter: "all",
    actionFilter: "all",
    searchTermDebounced: "",
  });

  // Actualizar refs cuando cambien los valores
  useEffect(() => {
    filtersRef.current = {
      zonaFilter,
      activoFilter,
      categoriaFilter,
      actionFilter,
      searchTermDebounced,
    };
  }, [
    zonaFilter,
    activoFilter,
    categoriaFilter,
    actionFilter,
    searchTermDebounced,
  ]);

  // Debounce para búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTermDebounced(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Cargar categorías
  const fetchCategorias = useCallback(async () => {
    try {
      const response = await axiosInstance.get(CATEGORIAS_URL);
      const results = Array.isArray(response.data)
        ? response.data
        : response.data?.results || [];
      setCategorias(results);
    } catch (error) {
      console.error("Error cargando categorías:", error);
    }
  }, []);

  useEffect(() => {
    fetchCategorias();
  }, [fetchCategorias]);

  // Cargar historial
  const fetchHistorial = useCallback(
    async (pageOrOptions = 1) => {
      const filters = filtersRef.current;
      const page =
        typeof pageOrOptions === "number"
          ? pageOrOptions
          : pageOrOptions?.page || currentPage || 1;

      setIsLoading(true);
      try {
        const params = { page };

        if (filters.zonaFilter !== "all") {
          params.zona = filters.zonaFilter;
        }
        if (filters.activoFilter !== "all") {
          params.activo = filters.activoFilter;
        }
        if (filters.categoriaFilter !== "all") {
          params.categoria = filters.categoriaFilter;
        }
        if (filters.actionFilter !== "all") {
          params.action = filters.actionFilter;
        }
        if (filters.searchTermDebounced.trim()) {
          params.search = filters.searchTermDebounced.trim();
        }

        const response = await axiosInstance.get(HISTORY_URL, { params });
        const payload = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];

        setHistorial(payload);

        const count =
          typeof response.data?.count === "number"
            ? response.data.count
            : Array.isArray(response.data)
              ? response.data.length
              : payload.length;

        const fetchedTotalPages =
          response.data?.total_pages ??
          (count > 0 ? Math.ceil(count / (response.data?.page_size ?? 25)) : 1);

        setTotalRecords(count);
        setTotalPages(Math.max(fetchedTotalPages, 1));
        setCurrentPage(
          response.data?.current_page ?? response.data?.page ?? page,
        );
        setNextUrl(response.data?.next ?? null);
        setPreviousUrl(response.data?.previous ?? null);
      } catch (error) {
        notifyError("No fue posible obtener el historial de activos.");
        setHistorial([]);
      } finally {
        setIsLoading(false);
      }
    },
    [currentPage],
  );

  // Consolidar efectos de filtros en uno solo
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchHistorial(1);
    }, 0);
    return () => clearTimeout(timer);
  }, [
    zonaFilter,
    activoFilter,
    categoriaFilter,
    actionFilter,
    searchTermDebounced,
  ]);

  // Handlers
  const handleZonaChange = useCallback((newValue) => {
    setZonaFilter(newValue || "all");
  }, []);

  const handleSearchChange = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleResetFilters = useCallback(() => {
    setZonaFilter("all");
    setActivoFilter("all");
    setCategoriaFilter("all");
    setActionFilter("all");
    setSearchTerm("");
    setSearchTermDebounced("");
    fetchHistorial(1);
  }, [fetchHistorial]);

  const handleConsult = useCallback(() => {
    fetchHistorial(1);
  }, [fetchHistorial]);

  // Exportar
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const params = {};
      if (zonaFilter !== "all") params.zona = zonaFilter;
      if (activoFilter !== "all") params.activo = activoFilter;
      if (categoriaFilter !== "all") params.categoria = categoriaFilter;
      if (actionFilter !== "all") params.action = actionFilter;
      if (searchTerm.trim()) params.search = searchTerm.trim();

      const response = await axiosInstance.get(`${HISTORY_URL}exportar/`, {
        params,
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `historial-activos-${moment().format("YYYY-MM-DD")}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      notifyError("No fue posible exportar el historial.");
    } finally {
      setIsExporting(false);
    }
  }, [zonaFilter, categoriaFilter, actionFilter, searchTerm]);

  // Columnas
  const columns = [
    {
      Header: "Fecha",
      accessor: "created_at",
      disableFilters: true,
    },
    {
      Header: "Acción",
      accessor: "action",
      Cell: ({ value }) => {
        const config = ACTION_LABELS[value] || {
          label: value,
          className: "bg-secondary",
        };
        return (
          <span className={`badge ${config.className}`}>{config.label}</span>
        );
      },
      disableFilters: true,
    },
    {
      Header: "Activo",
      accessor: (row) => {
        const nombre = row.snapshot?.nombre || row.consecutivo || "N/A";
        return nombre;
      },
      disableFilters: true,
    },
    {
      Header: "Consecutivo",
      accessor: "consecutivo",
      disableFilters: true,
    },
    {
      Header: "Zona",
      accessor: (row) => row.snapshot?.zona?.nombre || "-",
      disableFilters: true,
    },
    {
      Header: "Categoría",
      accessor: (row) => row.snapshot?.categoria?.nombre || "-",
      disableFilters: true,
    },
    {
      Header: "Usuario",
      accessor: (row) => row.changed_by_name || row.changed_by || "Sistema",
      disableFilters: true,
    },
  ];

  return (
    <section className="card">
      <header className="card-header">
        <div className="d-flex flex-wrap align-items-center gap-2 w-100">
          <div className="d-flex flex-column me-auto flex-grow-1">
            <h2 className="card-title mb-0">
              <i className="fas fa-history me-2 text-primary" />
              Historial de Inventario
            </h2>
            <div className="text-muted small">
              Consulta el historial completo de cambios en los activos del
              conjunto
            </div>
          </div>
          <div className="d-flex gap-2 ms-auto">
            <Button
              variant="outline-success"
              size="sm"
              onClick={handleExport}
              disabled={isExporting || historial.length === 0}
            >
              {isExporting ? (
                <span className="d-inline-flex align-items-center gap-2">
                  <i className="fas fa-circle-notch fa-spin" />
                  <span>Exportando...</span>
                </span>
              ) : (
                <>
                  <i className="fas fa-file-excel me-2" />
                  Exportar
                </>
              )}
            </Button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm text-body"
              onClick={handleResetFilters}
              disabled={isLoading}
            >
              Limpiar filtros
            </button>
            <button
              type="button"
              className="btn btn-primary btn-sm text-white"
              onClick={handleConsult}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="d-inline-flex align-items-center gap-2">
                  <i
                    className="fas fa-circle-notch fa-spin"
                    aria-hidden="true"
                  />
                  <span>Consultando...</span>
                </span>
              ) : (
                <>
                  <i className="fas fa-search me-2" />
                  Consultar
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      <div className="card-body">
        {/* Filtros */}
        <div className="row g-3 mb-4">
          <div className="col-lg-3 col-md-6">
            <ZonaSocialSelectReact
              id="zona-filter"
              name="zona"
              value={zonaFilter === "all" ? "" : zonaFilter}
              onChange={(newValue) => setZonaFilter(newValue || "all")}
              allowAllOption={false}
              allOptionLabel="Todas las zonas"
              showLabel
              label="Zona social"
              placeholder="Todas las zonas"
            />
          </div>

          <div className="col-lg-3 col-md-6">
            <label htmlFor="activo-filter" className="form-label">
              Activo
            </label>
            <ActivosSelectReact
              id="activo-filter"
              name="activo"
              value={activoFilter === "all" ? "" : activoFilter}
              onChange={(newValue) => setActivoFilter(newValue || "all")}
              allowAllOption={false}
              allOptionLabel="Todos los activos"
              showLabel={false}
              placeholder="Todos los activos"
            />
          </div>

          <div className="col-lg-2 col-md-6">
            <label htmlFor="categoria-filter" className="form-label">
              Categoría
            </label>
            <Select
              id="categoria-filter"
              name="categoria"
              value={
                categorias.find(
                  (cat) => (cat.uuid || String(cat.id)) === categoriaFilter,
                )
                  ? {
                      value: categoriaFilter,
                      label: categorias.find(
                        (cat) =>
                          (cat.uuid || String(cat.id)) === categoriaFilter,
                      )?.nombre,
                    }
                  : null
              }
              onChange={(option) => setCategoriaFilter(option?.value || "all")}
              options={categorias.map((cat) => ({
                value: cat.uuid || String(cat.id),
                label: cat.nombre,
              }))}
              isClearable
              isSearchable
              placeholder="Todas las categorías"
              isDisabled={isLoading}
              noOptionsMessage={() => "No hay categorías"}
            />
          </div>

          <div className="col-lg-2 col-md-6">
            <label htmlFor="action-filter" className="form-label">
              Acción
            </label>
            <Select
              id="action-filter"
              name="action"
              value={
                ACTION_OPTIONS.find((opt) => opt.value === actionFilter) || null
              }
              onChange={(option) => setActionFilter(option?.value || "all")}
              options={ACTION_OPTIONS}
              isClearable
              isSearchable
              placeholder="Todas"
              isDisabled={isLoading}
            />
          </div>

          <div className="col-lg-2 col-md-6">
            <label htmlFor="search-term" className="form-label">
              Búsqueda
            </label>
            <input
              id="search-term"
              type="text"
              className="form-control"
              placeholder="Nombre del activo o consecutivo"
              value={searchTerm}
              onChange={handleSearchChange}
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Estado de carga */}
        {isLoading && (
          <div className="py-5 text-center">
            <i className="fas fa-circle-notch fa-spin fa-2x mb-2" />
            <div>Cargando historial...</div>
          </div>
        )}

        {/* Sin resultados */}
        {!isLoading && historial.length === 0 && (
          <div className="alert alert-light border" role="alert">
            No se encontraron registros en el historial para los criterios
            seleccionados.
          </div>
        )}

        {/* Tabla */}
        {!isLoading && historial.length > 0 && (
          <Tables
            columns={columns}
            data={historial}
            loading={isLoading}
            totalPages={totalPages}
            currentPage={currentPage}
            totalRecords={totalRecords}
            fetchUrl={fetchHistorial}
            page={undefined}
            nextUrl={nextUrl}
            previousUrl={previousUrl}
          />
        )}
      </div>
    </section>
  );
};

export default HistorialActivos;
